import { ModalProps } from '@toggle/design-system';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from '~/components/actions-modal/ActionsModal.styles';
import { Watchlist } from '~/stores/use-watchlist/watchlist-types';

export type WatchlistTableModalType = 'cancel' | 'empty';

interface WatchlistTableModalProps {
  modalType: WatchlistTableModalType;
  listName: Watchlist['name'];
  exitEditMode: () => void;
  saveChanges: () => void;
  deleteWatchlist: () => void;
  onClose: ModalProps['onClose'];
}

export const WatchlistTableModal: FC<WatchlistTableModalProps> = ({
  modalType,
  listName,
  exitEditMode,
  saveChanges,
  deleteWatchlist,
  onClose,
}) => {
  const { t } = useTranslation(['widget', 'watchlistManager']);

  const modalTypeToDetails: Record<string, Omit<ModalProps, 'isOpen'>> = {
    cancel: {
      title: t('widget:watchlist.unsavedChangesTitle'),
      children: t('widget:watchlist.unsavedChangesMessage'),
      primaryBtnProps: {
        label: t('widget:watchlist.exitWithoutSaving'),
        onClick: () => {
          exitEditMode();
        },
        'data-testid': 'exit-without-saving',
      },
      cancelBtnProps: {
        label: t('widget:watchlist.saveChanges'),
        onClick: () => {
          saveChanges();
        },
        'data-testid': 'save-changes',
      },
    },
    empty: {
      title: t('widget:watchlist.watchlistEmptyTitle', { name: listName }),
      children: t('widget:watchlist.watchlistEmptyMessage'),
      primaryBtnProps: {
        label: t('watchlistManager:deleteWatchlist'),
        onClick: () => {
          deleteWatchlist();
        },
        'data-testid': 'delete',
      },
      cancelBtnProps: {
        label: t('widget:watchlist.cancelChanges'),
        onClick: () => {
          exitEditMode();
        },
        'data-testid': 'cancel',
      },
    },
  };

  const { children, ...rest } = modalTypeToDetails[modalType];

  return (
    <S.StyledModal
      data-testid="watchlist-table-modal"
      isOpen
      onClose={onClose}
      {...rest}
    >
      <S.ContentContainer>{children}</S.ContentContainer>
    </S.StyledModal>
  );
};
